import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AdminLayout } from '@/layouts/admin-layout'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import ForceGraph2D from 'react-force-graph-2d'
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  BarChart, Bar,
} from 'recharts'

const TYPE_COLORS = {
  gotcha: '#EF4444',
  manual: '#8B5CF6',
} as const

interface GraphNode {
  id: string
  name: string
  type: 'manual' | 'gotcha'
  domain: string
  usage: number
  rating: number
  x?: number
  y?: number
}

interface GraphLink {
  source: string
  target: string
}

interface GrowthPoint {
  month: string
  manuals: number
  gotchas: number
  total: number
}

interface DomainEntry {
  domain: string
  manuals: number
  gotchas: number
  total: number
}

interface Props {
  hero_stats: {
    total_entries: number
    total_manuals: number
    total_gotchas: number
    total_usage: number
    average_rating: number
    new_this_week: number
  }
  graph_data: {
    nodes: GraphNode[]
    links: GraphLink[]
  }
  growth_data: GrowthPoint[]
  domain_distribution: DomainEntry[]
}

function Index({ hero_stats, graph_data, growth_data, domain_distribution }: Props) {
  const { t } = useTranslation('admin')
  const graphRef = useRef<any>(null)
  const graphContainerRef = useRef<HTMLDivElement>(null)
  const [graphWidth, setGraphWidth] = useState(0)

  useEffect(() => {
    const el = graphContainerRef.current
    if (!el) return
    const ro = new ResizeObserver(([entry]) => {
      setGraphWidth(entry.contentRect.width)
    })
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  const graphData = useMemo(() => ({
    nodes: graph_data.nodes,
    links: graph_data.links,
  }), [graph_data])

  const nodeCanvasObject = useCallback((node: GraphNode, ctx: CanvasRenderingContext2D, globalScale: number) => {
    const color = TYPE_COLORS[node.type] || '#666'
    const baseSize = Math.max(2, Math.sqrt(node.usage + 1) * 1.2)
    const x = node.x ?? 0
    const y = node.y ?? 0

    // Outer glow
    ctx.beginPath()
    ctx.arc(x, y, baseSize * 3, 0, 2 * Math.PI)
    ctx.fillStyle = color
    ctx.globalAlpha = 0.06
    ctx.fill()

    // Mid glow
    ctx.beginPath()
    ctx.arc(x, y, baseSize * 1.8, 0, 2 * Math.PI)
    ctx.fillStyle = color
    ctx.globalAlpha = 0.12
    ctx.fill()

    // Core
    ctx.beginPath()
    ctx.arc(x, y, baseSize, 0, 2 * Math.PI)
    ctx.fillStyle = color
    ctx.globalAlpha = 0.9
    ctx.fill()
    ctx.globalAlpha = 1

    // Label (only when zoomed in)
    if (globalScale > 2.5) {
      const fontSize = Math.max(2, 10 / globalScale)
      ctx.font = `${fontSize}px Inter, system-ui, sans-serif`
      ctx.textAlign = 'center'
      ctx.textBaseline = 'top'
      ctx.fillStyle = '#94A3B8'
      ctx.fillText(node.name, x, y + baseSize + 2)
    }
  }, [])

  const nodePointerAreaPaint = useCallback((node: GraphNode, color: string, ctx: CanvasRenderingContext2D) => {
    const baseSize = Math.max(2, Math.sqrt(node.usage + 1) * 1.2)
    ctx.beginPath()
    ctx.arc(node.x ?? 0, node.y ?? 0, baseSize * 2, 0, 2 * Math.PI)
    ctx.fillStyle = color
    ctx.fill()
  }, [])

  const nodeLabel = useCallback((node: GraphNode) => {
    return `<div style="background: #161622; border: 1px solid #1E293B; border-radius: 8px; padding: 8px 12px; font-family: Inter, system-ui; font-size: 12px; color: #F1F5F9; max-width: 250px;">
      <div style="font-weight: 700; margin-bottom: 4px; color: ${TYPE_COLORS[node.type]}">${node.name}</div>
      <div style="color: #94A3B8; font-size: 11px;">
        ${node.type.charAt(0).toUpperCase() + node.type.slice(1)} &middot; ${node.domain} &middot; ${node.usage} uses${node.rating > 0 ? ` &middot; ${node.rating.toFixed(1)}&#9733;` : ''}
      </div>
    </div>`
  }, [])

  const statCards = [
    {
      title: t('collectiveKnowledge.dashboard.heroStats.totalEntries', { defaultValue: 'Total Knowledge Entries' }),
      value: hero_stats.total_entries.toLocaleString(),
      detail: `${hero_stats.total_manuals} manuals, ${hero_stats.total_gotchas} gotchas`,
      color: 'text-blue-400',
    },
    {
      title: t('collectiveKnowledge.dashboard.heroStats.totalUsage', { defaultValue: 'Total Usage Count' }),
      value: hero_stats.total_usage.toLocaleString(),
      detail: t('collectiveKnowledge.dashboard.heroStats.timesApplied', { defaultValue: 'Times applied across all projects' }),
      color: 'text-purple-400',
    },
    {
      title: t('collectiveKnowledge.dashboard.heroStats.averageRating', { defaultValue: 'Average Rating' }),
      value: hero_stats.average_rating > 0 ? `${hero_stats.average_rating.toFixed(1)} / 5.0` : 'N/A',
      detail: t('collectiveKnowledge.dashboard.heroStats.acrossReviewed', { defaultValue: 'Across reviewed entries' }),
      color: 'text-yellow-400',
    },
    {
      title: t('collectiveKnowledge.dashboard.heroStats.newThisWeek', { defaultValue: 'New This Week' }),
      value: `+${hero_stats.new_this_week}`,
      detail: t('collectiveKnowledge.dashboard.heroStats.entriesAdded', { defaultValue: 'Entries added in the last 7 days' }),
      color: 'text-green-400',
    },
  ]

  const chartTooltipStyle = {
    contentStyle: {
      backgroundColor: '#161622',
      border: '1px solid #1E293B',
      borderRadius: 8,
      fontSize: 12,
      color: '#F1F5F9',
    },
    itemStyle: { color: '#94A3B8' },
  }

  return (
    <div className="p-6 space-y-6 max-w-[1600px] mx-auto">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold">
          {t('collectiveKnowledge.dashboard.title', { defaultValue: 'Knowledge Dashboard' })}
        </h1>
        <p className="text-muted-foreground mt-1">
          {t('collectiveKnowledge.dashboard.subtitle', { defaultValue: 'Collective intelligence compounding over time' })}
        </p>
      </div>

      {/* Hero Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((card) => (
          <Card key={card.title}>
            <CardHeader className="pb-2">
              <CardDescription className="text-xs">{card.title}</CardDescription>
            </CardHeader>
            <CardContent>
              <div className={`text-3xl font-bold ${card.color}`}>{card.value}</div>
              <p className="text-xs text-muted-foreground mt-1">{card.detail}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Knowledge Graph */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>
                {t('collectiveKnowledge.dashboard.graph.title', { defaultValue: 'Knowledge Graph' })}
              </CardTitle>
              <CardDescription>
                {t('collectiveKnowledge.dashboard.graph.description', { defaultValue: 'Interactive map of all knowledge entries and their connections' })}
              </CardDescription>
            </div>
            <div className="flex gap-3">
              {([['gotcha', 'Gotchas'], ['manual', 'Manuals']] as const).map(([type, label]) => (
                <div key={type} className="flex items-center gap-1.5">
                  <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: TYPE_COLORS[type] }} />
                  <span className="text-xs text-muted-foreground">{label}</span>
                </div>
              ))}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div ref={graphContainerRef} className="rounded-lg overflow-hidden border" style={{ background: '#0C0C14', height: 500 }}>
            {graphWidth > 0 && (
              <ForceGraph2D
                ref={graphRef}
                graphData={graphData}
                nodeCanvasObject={nodeCanvasObject}
                nodePointerAreaPaint={nodePointerAreaPaint}
                nodeLabel={nodeLabel as any}
                linkColor={() => 'rgba(148, 163, 184, 0.06)'}
                linkWidth={0.5}
                backgroundColor="#0C0C14"
                cooldownTicks={100}
                d3AlphaDecay={0.02}
                d3VelocityDecay={0.3}
                width={graphWidth}
                height={500}
              />
            )}
          </div>
        </CardContent>
      </Card>

      {/* Growth Chart + Domain Distribution */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Growth Chart */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>
              {t('collectiveKnowledge.dashboard.growth.title', { defaultValue: 'Knowledge Growth' })}
            </CardTitle>
            <CardDescription>
              {t('collectiveKnowledge.dashboard.growth.description', { defaultValue: 'Cumulative knowledge entries over time' })}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={growth_data}>
                <defs>
                  <linearGradient id="gradManuals" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={TYPE_COLORS.manual} stopOpacity={0.3} />
                    <stop offset="95%" stopColor={TYPE_COLORS.manual} stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="gradGotchas" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={TYPE_COLORS.gotcha} stopOpacity={0.3} />
                    <stop offset="95%" stopColor={TYPE_COLORS.gotcha} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#1E293B" />
                <XAxis
                  dataKey="month"
                  tick={{ fontSize: 11, fill: '#64748B' }}
                  tickFormatter={(v: string) => {
                    const [y, m] = v.split('-')
                    return `${['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][parseInt(m) - 1]} '${y.slice(2)}`
                  }}
                />
                <YAxis tick={{ fontSize: 11, fill: '#64748B' }} />
                <Tooltip {...chartTooltipStyle} />
                <Area type="monotone" dataKey="gotchas" name="Gotchas" stroke={TYPE_COLORS.gotcha} fill="url(#gradGotchas)" stackId="1" />
                <Area type="monotone" dataKey="manuals" name="Manuals" stroke={TYPE_COLORS.manual} fill="url(#gradManuals)" stackId="1" />
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Domain Distribution */}
        <Card>
          <CardHeader>
            <CardTitle>
              {t('collectiveKnowledge.dashboard.domains.title', { defaultValue: 'By Domain' })}
            </CardTitle>
            <CardDescription>
              {t('collectiveKnowledge.dashboard.domains.description', { defaultValue: 'Knowledge spread across domains' })}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={domain_distribution.slice(0, 10)} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#1E293B" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 11, fill: '#64748B' }} />
                <YAxis
                  type="category"
                  dataKey="domain"
                  tick={{ fontSize: 11, fill: '#94A3B8' }}
                  width={90}
                />
                <Tooltip {...chartTooltipStyle} />
                <Bar dataKey="gotchas" name="Gotchas" fill={TYPE_COLORS.gotcha} stackId="a" radius={[0, 0, 0, 0]} />
                <Bar dataKey="manuals" name="Manuals" fill={TYPE_COLORS.manual} stackId="a" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}

Index.layout = (page: React.ReactNode) => <AdminLayout>{page}</AdminLayout>
export default Index
